import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';

const quotes = [
  { text: "The secret of getting ahead is getting started", author: "Mark Twain" },
  { text: "Done is better than perfect", author: "Sheryl Sandberg" },
  { text: "The way to get started is to quit talking and begin doing", author: "Walt Disney" },
  { text: "It always seems impossible until it's done", author: "Nelson Mandela" },
  { text: "You don't have to be great to start, but you have to start to be great", author: "Zig Ziglar" },
  { text: "The only way to do great work is to love what you do", author: "Steve Jobs" },
  { text: "Small progress is still progress", author: "Anonymous" },
  { text: "Focus on progress, not perfection", author: "Anonymous" }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(0);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth spring animation for mouse follower
  const springConfig = { damping: 25, stiffness: 200 };
  const followerX = useSpring(mouseX, springConfig);
  const followerY = useSpring(mouseY, springConfig);

  // Update mouse position
  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    mouseX.set(clientX);
    mouseY.set(clientY);
  };

  // Rotate quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % quotes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animation variants for the floating elements


  const buttonVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col items-center justify-center relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Mouse follower */}
      <motion.div
        className="pointer-events-none fixed left-0 top-0 w-96 h-96 bg-gradient-to-r from-blue-200/10 to-purple-200/10 rounded-full blur-3xl"
        style={{
          x: useTransform(followerX, (x) => x - 192),
          y: useTransform(followerY, (y) => y - 192),
        }}
      />

      {/* Background animations */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated checkmarks */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-green-400/30 text-4xl"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              rotate: Math.random() * 360
            }}
            animate={{
              y: [null, -window.innerHeight],
              rotate: [null, 360],
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "linear"
            }}
          >
            ✓
          </motion.div>
        ))}

        {/* Interactive floating task items */}
        {[
          "📝", "✅", "📅", "🎯", "⭐", "📌", "🔔",
          "Buy groceries", "Call mom", "Finish project", "Read book",
          "Exercise", "Team meeting", "Plan vacation"
        ].map((item, i) => {
          const x = useMotionValue(Math.random() * window.innerWidth);
          const y = useMotionValue(Math.random() * window.innerHeight);

          return (
            <motion.div
              key={i}
              className={`absolute cursor-pointer ${typeof item === 'string' && item.length > 2 ? 'text-sm' : 'text-2xl'}`}
              style={{ x, y }}
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.2, 0.3, 0.2],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
              whileHover={{
                scale: 1.2,
                opacity: 0.8,
                transition: { duration: 0.2 }
              }}
              drag
              dragConstraints={{
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
              dragElastic={0.1}
            >
              {item}
            </motion.div>
          );
        })}
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
            DoDueLife
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Life Happens. DoDueLife Makes it Happen Better! 🚀
          </p>
        </motion.div>

        <motion.button
          variants={buttonVariants}
          initial="initial"
          whileHover="hover"
          whileTap="tap"
          onClick={() => navigate('/login')}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="relative px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold shadow-lg group"
        >
          <span className="relative z-10">Let's Get Productive!</span>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.2 }}
          />
        </motion.button>

        <div className="mt-8 flex gap-4 justify-center">
          <Link to="/tutorial">
            <Button variant="outline" className="border-white/20">
              View Tutorial
            </Button>
          </Link>
        </div>
      </div>

      {/* Rotating quotes */}
      <motion.div
        key={currentQuote}
        className="absolute bottom-8 text-slate-500 text-sm max-w-md text-center px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
      >
        "{quotes[currentQuote].text}"
        <br />
        <span className="text-slate-400">- {quotes[currentQuote].author}</span>
      </motion.div>
    </div>
  );
};

export default LandingPage;
